import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../../../shared/i18n/initI18n';

type ElementTag = keyof JSX.IntrinsicElements;

interface BilingualTextProps {
  textKey: string;
  as?: ElementTag;
  className?: string;
  values?: Record<string, unknown>;
}

export function BilingualText({ textKey, as: Component = 'p', className, values }: BilingualTextProps) {
  const { i18n } = useTranslation();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void i18n
      .loadLanguages(supportedLanguages)
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setIsReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [i18n]);

  const zhTranslator = useMemo(() => i18n.getFixedT('zh'), [i18n, isReady]);
  const enTranslator = useMemo(() => i18n.getFixedT('en'), [i18n, isReady]);

  const renderText = (translator: (key: string, options?: Record<string, unknown>) => string, fallback: string) => {
    try {
      const translated = translator(textKey, values);
      return translated && translated !== textKey ? translated : fallback;
    } catch {
      return fallback;
    }
  };

  const zhText = renderText(zhTranslator as Translator, textKey);
  const enText = renderText(enTranslator as Translator, textKey);

  return (
    <Component className={clsx('space-y-1 leading-relaxed', className)}>
      <span className="block text-base text-ai-text-primary">{zhText}</span>
      <span className="block text-sm text-ai-text-secondary">{enText}</span>
    </Component>
  );
}

type Translator = (key: string, options?: Record<string, unknown>) => string;
